import Drawer from '@material-ui/core/Drawer';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {useRefreshAllNodes} from '@wandb/weave/react';
import React, {useEffect, useState} from 'react';

import {REGISTRY_PROJECT_PREFIX} from '../../../common/utils';
import {ResizableDrawer} from '../../ResizableDrawer';
import {Artifact, Collection} from '../CreateRegisteredCollectionDrawerInner';
import * as S from '../CreateRegisteredCollectionDrawerInner.styles';
import {DrawerPageAddVersion} from '../DrawerPageAddVersion';
import {useLinkArtifactVersion} from '../hooks';
import {ArtifactVersion} from '../SelectArtifactCollectionVersion';

/**
 * Workflow to add an artifact version to a collection based on the artifact type
 */
type AddArtifactDrawerProps = {
  open: boolean;
  closeDrawer: () => void;

  collection: Collection;
  defaultProjectName: string;
  scrollWidth: number;
  refetchCollections?: () => void;
};

export const AddArtifactDrawer = ({
  open,
  closeDrawer,
  collection,
  defaultProjectName,
  scrollWidth,
  refetchCollections,
}: AddArtifactDrawerProps) => {
  // general hooks
  const refreshNodes = useRefreshAllNodes();

  // Common state variables
  const [linkButtonDisabled, setLinkButtonDisabled] = useState(false);
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  // Version Config
  const isOrgRegistry = defaultProjectName.startsWith(REGISTRY_PROJECT_PREFIX);
  const [artifact, setArtifact] = useState<Artifact>({
    entityName: isOrgRegistry ? '' : collection.entityName,
    projectName: '',
    sequenceName: '',
    alias: '',
    id: '',
    versionIndex: 0,
    ttlDurationSeconds: 0,
  });
  const [artifactVersions, setArtifactVersions] = useState<ArtifactVersion[]>(
    []
  );

  // Link version
  const {linkArtifactVersion, loading} = useLinkArtifactVersion();

  // Enable or disable button to go to the next page based on user input
  useEffect(() => {
    setLinkButtonDisabled(artifact.id === '');
  }, [artifact.id]);
  return (
    <Drawer anchor="right" open={open} onClose={closeDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <S.DrawerContents scrollWidth={scrollWidth}>
          <S.RegisteredCollectionDrawer>
            <S.CenteredFlex>
              <S.Header>Link new version to "{collection.name}"</S.Header>
              <S.CloseButton>
                <Button
                  onClick={closeDrawer}
                  icon="close"
                  size="large"
                  variant="ghost"
                />
              </S.CloseButton>
            </S.CenteredFlex>
            <S.Divider />
            <S.RegisteredCollectionDrawerMain>
              <DrawerPageAddVersion
                collection={collection}
                artifact={artifact}
                artifactVersions={artifactVersions}
                defaultProjectName={defaultProjectName}
                hideStepHeader={true}
                hideBanner={true}
                setArtifactVersions={setArtifactVersions}
                setArtifact={setArtifact}
              />
            </S.RegisteredCollectionDrawerMain>
            <S.Divider />
            <S.Footer>
              <S.FooterLeftContent>
                <S.HelpMessage>
                  Linking new version as {collection.name}:v
                  {collection.numVersions}
                </S.HelpMessage>
              </S.FooterLeftContent>
              <S.FooterRightContent>
                <Button
                  className="create-button"
                  variant="primary"
                  size="large"
                  onClick={async () => {
                    if (artifact.id) {
                      await linkArtifactVersion(
                        artifact.id,
                        collection.id ?? '',
                        collection.name,
                        []
                      );
                    }
                    await refreshNodes();
                    await refetchCollections?.();
                    closeDrawer();
                  }}
                  disabled={linkButtonDisabled || loading}>
                  Link version
                </Button>
              </S.FooterRightContent>
            </S.Footer>
          </S.RegisteredCollectionDrawer>
        </S.DrawerContents>
      </ResizableDrawer>
    </Drawer>
  );
};
